import { SalesOrderStatus } from '../sales-orders/sales-orders.schema';

export enum InvoiceErrorStatus {
  UNREVIEWED = 'UNREVIEWED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  SENT = 'SENT',
  NO_REVIEW_REQUIRED = 'NO_REVIEW_REQUIRED',
  UNDER_REVIEW = 'UNDER_REVIEW',
}

export enum InvoiceSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum InvoiceSortBy {
  INVOICE_NAME = 'INVOICE_NAME',
  STATUS = 'STATUS',
  DATE = 'DATE',
  LOGISTICS_PROVIDER = 'LOGISTICS_PROVIDER',
  CHARGES = 'CHARGES',
  ERRORS = 'ERRORS',
  TYPE = 'TYPE',
}

export enum RollupOrdersSortBy {
  EXPECTED_QTY = 'EXPECTED_QTY',
  INVOICED_QTY = 'INVOICED_QTY',
  ITEM_COUNT_DIFF = 'ITEM_COUNT_DIFF',
  ORDER_NUMBER = 'ORDER_NUMBER',
  SERVICE = 'SERVICE',
}

export enum InvoiceQueryType {
  STATUS = 'STATUS',
  LOGISTICS_PROVIDER = 'LOGISTICS_PROVIDER',
  WAREHOUSE = 'WAREHOUSE',
  // CHARGES = 'CHARGES',
  // ERROR_DOLLARS = 'ERROR_DOLLARS',
  // ERROR_COUNTS = 'ERROR_COUNTS',
}

export enum InvoiceQueryLinker {
  AND = 'AND',
  OR = 'OR',
}

export enum InvoiceAggregationMode {
  Invoice = 'Invoice',
  Monthly = 'Monthly',
  Custom = 'Custom',
}

export const INVOICE_TYPE_NAME = {
  FULFILLMENT: 'Outbound Fulfillment',
  FREIGHT: 'Outbound Freight',
};

export const RECONCILIATIN_INVOICE_TYPE_NAMES = [INVOICE_TYPE_NAME.FULFILLMENT, INVOICE_TYPE_NAME.FREIGHT];

export const INVOICE_TYPE_MAP = {
  [INVOICE_TYPE_NAME.FULFILLMENT]: 'Fulfillment',
  [INVOICE_TYPE_NAME.FREIGHT]: 'Freight',
};

export enum InvoiceStatus {
  CREDIT_REQUESTS_DONE = 'CREDIT_REQUESTS_DONE',
  CREDIT_REQUESTS_IN_PROGRESS = 'CREDIT_REQUESTS_IN_PROGRESS',
  REVIEW_DONE = 'REVIEW_DONE',
  UNDER_REVIEW = 'UNDER_REVIEW',
  UNREVIEWED = 'UNREVIEWED',
  NO_REVIEW_REQUIRED = 'NO_REVIEW_REQUIRED',
}

export const INVOICE_STATUS_DESCRIPTION = `The status of the invoice:

- ${InvoiceStatus.CREDIT_REQUESTS_DONE}: All credit requests are requested or with some that are rejected.

- ${InvoiceStatus.CREDIT_REQUESTS_IN_PROGRESS}: If customer has done even just 1 credit request on any of the order errors within that invoice, that is now considered as pending.

- ${InvoiceStatus.REVIEW_DONE}: if you accepted/rejected all order errors inside the invoice, that invoice is now reviewed. If you have done even just 1 credit request on any of the OrderError inside that invoice, that transition the status to ${InvoiceStatus.CREDIT_REQUESTS_IN_PROGRESS}.

- ${InvoiceStatus.UNDER_REVIEW}: If customer has done even just a single accept or reject on an order error inside an invoice, that invoice is under review.

- ${InvoiceStatus.UNREVIEWED}: No order errors have been accepted or rejected.

- ${InvoiceStatus.NO_REVIEW_REQUIRED}: If there are no order errors in the invoice, then the status is set to ${InvoiceStatus.NO_REVIEW_REQUIRED}.
`;

export function computeInvoiceStatus(
  orderStatuses: SalesOrderStatus[],
  invoiceErrorStatuses: InvoiceErrorStatus[] = [],
): InvoiceStatus {
  const orderStatusCount = {
    unreviewed: 0,
    reviewed: 0,
    noReviewRequired: 0,
    underReview: 0,
    creditsPartiallyRequested: 0,
    creditsRequested: 0,
    numOfTotalOrders: orderStatuses.length,
  };

  const invoiceErrorStatusCount = {
    unreviewed: 0,
    reviewedAccepted: 0,
    reviewedRejected: 0,
    creditRequested: 0,
    noReviewRequired: 0,
    totalStatusCount: invoiceErrorStatuses.length,
  };

  orderStatuses.forEach((orderStatus) => {
    switch (orderStatus) {
      case SalesOrderStatus.UNREVIEWED:
        orderStatusCount.unreviewed++;
        break;
      case SalesOrderStatus.REVIEWED:
        orderStatusCount.reviewed++;
        break;
      case SalesOrderStatus.NO_REVIEW_REQUIRED:
        orderStatusCount.noReviewRequired++;
        break;
      case SalesOrderStatus.UNDER_REVIEW:
        orderStatusCount.underReview++;
        break;
      case SalesOrderStatus.CREDITS_PARTIALLY_REQUESTED:
        orderStatusCount.creditsPartiallyRequested++;
        break;
      case SalesOrderStatus.CREDITS_REQUESTED:
        orderStatusCount.creditsRequested++;
        break;
      default:
        break;
    }
  });

  invoiceErrorStatuses.forEach((invoiceErrorStatus) => {
    switch (invoiceErrorStatus) {
      case InvoiceErrorStatus.UNREVIEWED:
        invoiceErrorStatusCount.unreviewed++;
        break;
      case InvoiceErrorStatus.REJECTED:
        invoiceErrorStatusCount.reviewedRejected++;
        break;
      case InvoiceErrorStatus.ACCEPTED:
        invoiceErrorStatusCount.reviewedAccepted++;
        break;
      case InvoiceErrorStatus.NO_REVIEW_REQUIRED:
        invoiceErrorStatusCount.noReviewRequired++;
        break;
      case InvoiceErrorStatus.SENT:
        invoiceErrorStatusCount.creditRequested++;
        break;
    }
  });

  const {
    unreviewed,
    reviewed,
    noReviewRequired,
    underReview,
    creditsPartiallyRequested,
    creditsRequested,
    numOfTotalOrders,
  } = orderStatusCount;

  const {
    unreviewed: errorUnreviewed,
    reviewedAccepted: errorAccepted,
    reviewedRejected: errorRejected,
    creditRequested: errorCreditRequested,
    noReviewRequired: errorNoReviewRequired,
    totalStatusCount: totalErrorCount,
  } = invoiceErrorStatusCount;

  // Combined logic for orders and errors
  const isNoReviewRequired =
    (noReviewRequired === numOfTotalOrders || numOfTotalOrders === 0) &&
    (errorNoReviewRequired === totalErrorCount || totalErrorCount === 0);
  const isUnreviewed =
    unreviewed + noReviewRequired === numOfTotalOrders && errorUnreviewed + errorNoReviewRequired === totalErrorCount;
  const isReviewDone =
    reviewed + noReviewRequired === numOfTotalOrders &&
    errorRejected + errorAccepted + errorNoReviewRequired === totalErrorCount;
  const isCreditsRequested =
    creditsRequested + noReviewRequired === numOfTotalOrders &&
    errorRejected + errorCreditRequested + errorNoReviewRequired === totalErrorCount;
  const isCreditsRequestsInProgress = creditsRequested > 0 || creditsPartiallyRequested > 0 || errorCreditRequested > 0;
  const isUnderReview = unreviewed > 0 || underReview > 0 || errorUnreviewed > 0;

  if (isNoReviewRequired) {
    return InvoiceStatus.NO_REVIEW_REQUIRED;
  } else if (isUnreviewed) {
    return InvoiceStatus.UNREVIEWED;
  } else if (isCreditsRequested) {
    return InvoiceStatus.CREDIT_REQUESTS_DONE;
  } else if (isCreditsRequestsInProgress) {
    return InvoiceStatus.CREDIT_REQUESTS_IN_PROGRESS;
  } else if (isReviewDone) {
    return InvoiceStatus.REVIEW_DONE;
  } else if (isUnderReview) {
    return InvoiceStatus.UNDER_REVIEW;
  }

  return InvoiceStatus.NO_REVIEW_REQUIRED;
}

/**
 * Computes the status for a grouped invoice based on an array of individual invoice statuses.
 * Uses similar logic to computeInvoiceStatus but operates on invoice-level statuses instead of sales order statuses.
 *
 * @param invoiceStatuses - Array of individual invoice statuses within the group
 * @returns The computed status for the grouped invoice
 */
export function computeGroupedInvoiceStatus(invoiceStatuses: InvoiceStatus[]): InvoiceStatus {
  const statusCount = {
    unreviewed: 0,
    reviewDone: 0,
    noReviewRequired: 0,
    underReview: 0,
    creditsInProgress: 0,
    creditsDone: 0,
    numOfTotalInvoices: invoiceStatuses.length,
  };

  invoiceStatuses.forEach((status) => {
    switch (status) {
      case InvoiceStatus.UNREVIEWED:
        statusCount.unreviewed++;
        break;
      case InvoiceStatus.REVIEW_DONE:
        statusCount.reviewDone++;
        break;
      case InvoiceStatus.NO_REVIEW_REQUIRED:
        statusCount.noReviewRequired++;
        break;
      case InvoiceStatus.UNDER_REVIEW:
        statusCount.underReview++;
        break;
      case InvoiceStatus.CREDIT_REQUESTS_IN_PROGRESS:
        statusCount.creditsInProgress++;
        break;
      case InvoiceStatus.CREDIT_REQUESTS_DONE:
        statusCount.creditsDone++;
        break;
      default:
        break;
    }
  });

  const { unreviewed, reviewDone, noReviewRequired, underReview, creditsInProgress, creditsDone, numOfTotalInvoices } =
    statusCount;

  const isNoReviewRequired = noReviewRequired === numOfTotalInvoices || numOfTotalInvoices === 0;
  const isUnreviewed = unreviewed + noReviewRequired === numOfTotalInvoices;
  const isReviewDone = reviewDone + noReviewRequired === numOfTotalInvoices;
  const isCreditsDone = creditsDone + noReviewRequired === numOfTotalInvoices;
  const isCreditsInProgress = creditsDone > 0 || creditsInProgress > 0;
  const isUnderReview = unreviewed > 0 || underReview > 0;

  if (isNoReviewRequired) {
    return InvoiceStatus.NO_REVIEW_REQUIRED;
  } else if (isUnreviewed) {
    return InvoiceStatus.UNREVIEWED;
  } else if (isCreditsDone) {
    return InvoiceStatus.CREDIT_REQUESTS_DONE;
  } else if (isCreditsInProgress) {
    return InvoiceStatus.CREDIT_REQUESTS_IN_PROGRESS;
  } else if (isReviewDone) {
    return InvoiceStatus.REVIEW_DONE;
  } else if (isUnderReview) {
    return InvoiceStatus.UNDER_REVIEW;
  }

  return InvoiceStatus.NO_REVIEW_REQUIRED;
}

/**
 * Computes a single invoice error status from an array of invoice error statuses.
 * @param errorStatuses - Array of invoice error statuses to aggregate
 * @returns Single computed invoice error status
 */
export function computeInvoiceErrorStatus(errorStatuses: InvoiceErrorStatus[]): InvoiceErrorStatus {
  if (!errorStatuses || errorStatuses.length === 0) {
    return InvoiceErrorStatus.NO_REVIEW_REQUIRED;
  }

  const statusCount = {
    unreviewed: 0,
    accepted: 0,
    rejected: 0,
    sent: 0,
    noReviewRequired: 0,
    totalCount: errorStatuses.length,
  };

  // Count occurrences of each status
  errorStatuses.forEach((status) => {
    switch (status) {
      case InvoiceErrorStatus.UNREVIEWED:
        statusCount.unreviewed++;
        break;
      case InvoiceErrorStatus.ACCEPTED:
        statusCount.accepted++;
        break;
      case InvoiceErrorStatus.REJECTED:
        statusCount.rejected++;
        break;
      case InvoiceErrorStatus.SENT:
        statusCount.sent++;
        break;
      case InvoiceErrorStatus.NO_REVIEW_REQUIRED:
        statusCount.noReviewRequired++;
        break;
      default:
        break;
    }
  });

  const { unreviewed, accepted, rejected, sent, noReviewRequired, totalCount } = statusCount;

  // Determine final status based on counts and business logic
  const isNoReviewRequired = noReviewRequired === totalCount;
  const isUnreviewed = unreviewed + noReviewRequired === totalCount;
  const isAccepted = accepted + noReviewRequired === totalCount;
  const isRejected = rejected + noReviewRequired === totalCount;
  const isSent = sent + rejected + noReviewRequired === totalCount;
  const isPartiallyReviewed = unreviewed > 0 && (accepted > 0 || rejected > 0);

  // Apply priority logic for determining final status
  if (isNoReviewRequired) {
    return InvoiceErrorStatus.NO_REVIEW_REQUIRED;
  } else if (isUnreviewed) {
    return InvoiceErrorStatus.UNREVIEWED;
  } else if (isAccepted) {
    return InvoiceErrorStatus.ACCEPTED;
  } else if (isRejected) {
    return InvoiceErrorStatus.REJECTED;
  } else if (isSent) {
    return InvoiceErrorStatus.SENT;
  } else if (isPartiallyReviewed) {
    return InvoiceErrorStatus.UNDER_REVIEW;
  }

  return InvoiceErrorStatus.NO_REVIEW_REQUIRED;
}

export enum InvoiceAuditLevel {
  FULL_RECONCILIATION = 'FULL_RECONCILIATION',
  SUMMARY_ONLY = 'SUMMARY_ONLY',
}

export type CreateCdmLogisticsInvoiceData = {
  biller: string;
  brand_id: string;
  ingestion_id: string;
  number: string;
  due_date?: Date | null;
  invoice_date?: Date | null;
  period_end_date?: Date | null;
  period_start_date?: Date | null;
  raw_data: Record<string, unknown>;
  type?: string | null;
};
